import { apiClient } from '../utils/apiClient';

class ShareService {
  async createShareLink(
    type,
    id,
    { password = null, expiresInDays = 7, maxUses = 1, chatChannelId = null, payload = null } = {}
  ) {
    try {
      const body = { type };
      if (id != null) body.id = id;
      if (password) body.password = password;
      if (expiresInDays !== null) body.expires_in_days = expiresInDays;
      if (maxUses !== null) body.max_uses = maxUses;
      // Chat-scoped share: only members of this conversation can access it.
      if (chatChannelId != null) body.chat_channel_id = chatChannelId;
      // Snapshot share (local-only resource): serialized data instead of an id.
      if (payload != null) body.payload = payload;

      const response = await apiClient.post('/share', body);
      return response.data; // { token, url, expires_at, password_protected, max_uses }
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to create share link');
    }
  }

  async getShareInfo(token) {
    try {
      const response = await apiClient.get(`/share/${token}`);
      // { type, name, description, created_by_name, expires_at,
      //   password_protected, use_count, method?, snapshot?, chat_scoped? }
      return response.data;
    } catch (error) {
      const status = error.response?.status;
      if (status === 410) {
        throw new Error(error.response?.data?.error || 'This share link has expired or been revoked');
      }
      if (status === 401) {
        throw new Error('Sign in to access this share');
      }
      if (status === 403) {
        throw new Error('Only the people this was shared with can access it');
      }
      throw new Error(error.response?.data?.error || 'Share link not found');
    }
  }

  async acceptShareLink(
    token,
    {
      password = null,
      workspaceId = null,
      collectionId = null,
      newCollectionName = null,
      newWorkspaceName = null,
      newWorkspaceTeamId = null,
    } = {}
  ) {
    try {
      const payload = {};
      if (password) payload.password = password;
      if (workspaceId) payload.workspace_id = workspaceId;
      // Collection/request shares: fork into a brand-new workspace instead
      // (optionally inside one of the accepter's teams).
      if (newWorkspaceName) payload.new_workspace_name = newWorkspaceName;
      if (newWorkspaceTeamId) payload.new_workspace_team_id = newWorkspaceTeamId;
      // Request shares: destination collection in the chosen workspace.
      if (collectionId) payload.collection_id = collectionId;
      if (newCollectionName) payload.new_collection_name = newCollectionName;

      const response = await apiClient.post(`/share/${token}/accept`, payload);
      return response.data; // { type, result }
    } catch (error) {
      const status = error.response?.status;
      if (status === 403) {
        throw new Error(
          error.response?.data?.error || 'Only the people this was shared with can accept it'
        );
      }
      if (status === 404) {
        // Server distinguishes "not found or revoked" (link) from a deleted
        // resource — surface its message rather than guessing wrong.
        throw new Error(error.response?.data?.error || 'This share link no longer exists');
      }
      throw new Error(error.response?.data?.error || 'Failed to accept share link');
    }
  }

  async fetchShareData(token, { password = null } = {}) {
    try {
      const payload = {};
      if (password) payload.password = password;
      const response = await apiClient.post(`/share/${token}/data`, payload);
      return response.data; // { type, data }
    } catch (error) {
      // A removed share 404s here. Without this branch it fell through to
      // "Failed to fetch share data", so a recipient mid-fork to a LOCAL
      // destination could not tell a deletion from an outage — while the cloud
      // path (acceptShareLink, above) has always mapped 404 properly. Since
      // `Chat.soft_delete_message/2` started hard-deleting the share row with
      // the message, this is a normal, expected state, and it must read
      // differently from "this share can't be opened on this account"
      // (a key problem, which retrying will not fix either, but for a
      // completely different reason).
      if (error.response?.status === 404) {
        throw new Error(error.response?.data?.error || 'This share was removed by the sender.');
      }
      throw new Error(error.response?.data?.error || 'Failed to fetch share data');
    }
  }

  async revokeShareLink(token) {
    try {
      const response = await apiClient.delete(`/share/${token}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to revoke share link');
    }
  }

  async emailShare(type, itemId, emails, message = '') {
    try {
      const endpoint = type === 'workspace'
        ? `/workspaces/${itemId}/email-share`
        : `/collections/${itemId}/email-share`;
      const response = await apiClient.post(endpoint, { emails, message });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to send share emails');
    }
  }
}

const shareService = new ShareService();
export default shareService;
